import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { toast } from "sonner";
import {
  Sparkles,
  LogOut,
  Search,
  Users,
  UserCheck,
  UserPlus,
  TrendingUp,
  MoreVertical,
  Trash2,
  Mail,
  Phone,
  Calendar,
  MessageSquare,
  RefreshCw,
  Download,
  StickyNote,
  Plus,
  X,
} from "lucide-react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState({ total: 0, new: 0, contacted: 0, converted: 0 });
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [selectedLead, setSelectedLead] = useState(null);
  const [notesDialogOpen, setNotesDialogOpen] = useState(false);
  const [newNote, setNewNote] = useState("");
  const [isAddingNote, setIsAddingNote] = useState(false);
  const [isExporting, setIsExporting] = useState(false);

  const getAuthHeader = useCallback(() => {
    const token = localStorage.getItem("admin_token");
    return { Authorization: `Bearer ${token}` };
  }, []);

  const fetchData = useCallback(async () => {
    try {
      const [leadsRes, statsRes] = await Promise.all([
        axios.get(`${API}/leads`, { headers: getAuthHeader() }),
        axios.get(`${API}/leads/stats`, { headers: getAuthHeader() }),
      ]);
      setLeads(leadsRes.data);
      setStats(statsRes.data);
    } catch (error) {
      if (error.response?.status === 401) {
        toast.error("Session expired. Please login again.");
        localStorage.removeItem("admin_token");
        navigate("/admin/login");
      } else {
        toast.error("Failed to fetch data");
      }
    } finally {
      setIsLoading(false);
    }
  }, [getAuthHeader, navigate]);

  useEffect(() => {
    const token = localStorage.getItem("admin_token");
    if (!token) {
      navigate("/admin/login");
      return;
    }
    fetchData();
  }, [fetchData, navigate]);

  const handleStatusChange = async (leadId, newStatus) => {
    try {
      await axios.patch(
        `${API}/leads/${leadId}`,
        { status: newStatus },
        { headers: getAuthHeader() }
      );
      toast.success("Status updated");
      fetchData();
    } catch (error) {
      toast.error("Failed to update status");
    }
  };

  const handleDelete = async (leadId) => {
    try {
      await axios.delete(`${API}/leads/${leadId}`, { headers: getAuthHeader() });
      toast.success("Lead deleted");
      fetchData();
    } catch (error) {
      toast.error("Failed to delete lead");
    }
  };

  const handleExportCSV = async () => {
    setIsExporting(true);
    try {
      const response = await axios.get(`${API}/leads/export`, {
        headers: getAuthHeader(),
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `aurealian_leads_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success("Leads exported successfully");
    } catch (error) {
      toast.error("Failed to export leads");
    } finally {
      setIsExporting(false);
    }
  };

  const handleAddNote = async () => {
    if (!newNote.trim() || !selectedLead) return;
    
    setIsAddingNote(true);
    try {
      const response = await axios.post(
        `${API}/leads/${selectedLead.id}/notes`,
        { text: newNote },
        { headers: getAuthHeader() }
      );
      setSelectedLead(response.data);
      setNewNote("");
      toast.success("Note added");
      fetchData();
    } catch (error) {
      toast.error("Failed to add note");
    } finally {
      setIsAddingNote(false);
    }
  };

  const handleDeleteNote = async (noteId) => {
    if (!selectedLead) return;
    
    try {
      await axios.delete(
        `${API}/leads/${selectedLead.id}/notes/${noteId}`,
        { headers: getAuthHeader() }
      );
      setSelectedLead(prev => ({
        ...prev,
        notes: prev.notes.filter(n => n.id !== noteId)
      }));
      toast.success("Note deleted");
      fetchData();
    } catch (error) {
      toast.error("Failed to delete note");
    }
  };

  const openNotesDialog = (lead) => {
    setSelectedLead(lead);
    setNotesDialogOpen(true);
    setNewNote("");
  };

  const handleLogout = () => {
    localStorage.removeItem("admin_token");
    navigate("/admin/login");
  };

  const filteredLeads = leads.filter((lead) => {
    const matchesSearch =
      lead.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      lead.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      lead.phone.includes(searchTerm);
    const matchesStatus = statusFilter === "all" || lead.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const formatNoteDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getStatusBadge = (status) => {
    const styles = {
      new: "status-new",
      contacted: "status-contacted",
      converted: "status-converted",
    };
    return (
      <span className={`px-3 py-1 rounded-sm text-xs uppercase tracking-wider font-medium ${styles[status] || styles.new}`}>
        {status}
      </span>
    );
  };

  const statCards = [
    { label: "Total Leads", value: stats.total, icon: Users, color: "text-[#D4AF37]" },
    { label: "New", value: stats.new, icon: UserPlus, color: "text-[#D4AF37]" },
    { label: "Contacted", value: stats.contacted, icon: UserCheck, color: "text-blue-400" },
    { label: "Converted", value: stats.converted, icon: TrendingUp, color: "text-green-400" },
  ];

  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      {/* Sidebar */}
      <aside className="fixed left-0 top-0 bottom-0 w-64 bg-[#0A0A0A] border-r border-[#27272A]/40 p-6 hidden lg:flex flex-col">
        <div className="flex items-center gap-2 mb-8">
          <div className="w-8 h-8 bg-[#D4AF37] rounded-sm flex items-center justify-center">
            <Sparkles className="w-4 h-4 text-black" />
          </div>
          <span 
            className="font-bold text-lg tracking-wide text-white"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            AUREALIAN
          </span>
        </div>

        <nav className="flex-1">
          <div className="space-y-2">
            <div className="flex items-center gap-3 px-4 py-3 bg-[#D4AF37]/10 border-l-2 border-[#D4AF37] text-[#D4AF37]">
              <Users className="w-4 h-4" />
              <span className="text-sm font-medium">Leads</span>
            </div>
          </div>
        </nav>

        <Button
          onClick={handleLogout}
          variant="ghost"
          data-testid="logout-btn"
          className="justify-start text-[#A1A1AA] hover:text-white hover:bg-[#1F1F1F]"
        >
          <LogOut className="w-4 h-4 mr-2" />
          Sign Out
        </Button>
      </aside>

      {/* Main Content */}
      <main className="lg:ml-64 p-6 lg:p-8">
        {/* Mobile Header */}
        <div className="lg:hidden flex items-center justify-between mb-6">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-[#D4AF37] rounded-sm flex items-center justify-center">
              <Sparkles className="w-4 h-4 text-black" />
            </div>
            <span className="font-bold text-lg text-white" style={{ fontFamily: "'Playfair Display', serif" }}>
              AUREALIAN
            </span>
          </div>
          <Button
            onClick={handleLogout}
            variant="ghost"
            size="sm"
            data-testid="mobile-logout-btn"
            className="text-[#A1A1AA]"
          >
            <LogOut className="w-4 h-4" />
          </Button>
        </div>

        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
          <div>
            <h1 
              className="text-2xl md:text-3xl font-bold text-white mb-2"
              style={{ fontFamily: "'Playfair Display', serif" }}
              data-testid="dashboard-title"
            >
              Lead Management
            </h1>
            <p className="text-[#A1A1AA]">Track and manage your coaching leads</p>
          </div>
          <Button
            onClick={handleExportCSV}
            disabled={isExporting || leads.length === 0}
            data-testid="export-csv-btn"
            className="bg-[#D4AF37] text-black hover:bg-[#D4AF37]/90 rounded-none uppercase tracking-widest text-xs font-bold px-6 disabled:opacity-50"
          >
            <Download className="w-4 h-4 mr-2" />
            {isExporting ? "Exporting..." : "Export CSV"}
          </Button>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          {statCards.map((stat, idx) => (
            <div
              key={idx}
              className="bg-[#121212] border border-[#27272A]/40 rounded-sm p-4 md:p-6"
              data-testid={`stat-card-${stat.label.toLowerCase().replace(" ", "-")}`}
            >
              <div className="flex items-center justify-between mb-3">
                <stat.icon className={`w-5 h-5 ${stat.color}`} />
              </div>
              <p className="text-2xl md:text-3xl font-bold text-white mb-1">{stat.value}</p>
              <p className="text-xs text-[#A1A1AA] uppercase tracking-wider">{stat.label}</p>
            </div>
          ))}
        </div>

        {/* Filters */}
        <div className="flex flex-col md:flex-row gap-4 mb-6">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#52525B]" />
            <Input
              placeholder="Search by name, email, or phone..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              data-testid="search-input"
              className="bg-[#121212] border-[#27272A]/40 focus:border-[#D4AF37] rounded-sm h-10 pl-10 text-white placeholder:text-[#52525B]"
            />
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger 
              data-testid="status-filter"
              className="w-full md:w-48 bg-[#121212] border-[#27272A]/40 focus:border-[#D4AF37] rounded-sm h-10 text-white"
            >
              <SelectValue placeholder="Filter by status" />
            </SelectTrigger>
            <SelectContent className="bg-[#121212] border-[#27272A]">
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="new">New</SelectItem>
              <SelectItem value="contacted">Contacted</SelectItem>
              <SelectItem value="converted">Converted</SelectItem>
            </SelectContent>
          </Select>
          <Button
            onClick={fetchData}
            variant="outline"
            data-testid="refresh-btn"
            className="border-[#27272A]/40 text-[#A1A1AA] hover:text-white hover:bg-[#1F1F1F] rounded-sm"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>

        {/* Leads Table */}
        <div className="bg-[#121212] border border-[#27272A]/40 rounded-sm overflow-hidden">
          {isLoading ? (
            <div className="p-12 text-center">
              <div className="w-8 h-8 border-2 border-[#D4AF37] border-t-transparent rounded-full animate-spin mx-auto mb-4" />
              <p className="text-[#A1A1AA]">Loading leads...</p>
            </div>
          ) : filteredLeads.length === 0 ? (
            <div className="p-12 text-center" data-testid="no-leads-message">
              <Users className="w-12 h-12 text-[#27272A] mx-auto mb-4" />
              <p className="text-[#A1A1AA]">
                {searchTerm || statusFilter !== "all" 
                  ? "No leads match your filters" 
                  : "No leads yet. They'll appear here when people submit the form."}
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="border-[#27272A]/40 hover:bg-transparent">
                    <TableHead className="text-[#A1A1AA] text-xs uppercase tracking-wider font-medium">Contact</TableHead>
                    <TableHead className="text-[#A1A1AA] text-xs uppercase tracking-wider font-medium">Details</TableHead>
                    <TableHead className="text-[#A1A1AA] text-xs uppercase tracking-wider font-medium">Source</TableHead>
                    <TableHead className="text-[#A1A1AA] text-xs uppercase tracking-wider font-medium">Status</TableHead>
                    <TableHead className="text-[#A1A1AA] text-xs uppercase tracking-wider font-medium">Notes</TableHead>
                    <TableHead className="text-[#A1A1AA] text-xs uppercase tracking-wider font-medium">Date</TableHead>
                    <TableHead className="text-[#A1A1AA] text-xs uppercase tracking-wider font-medium w-16"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredLeads.map((lead) => (
                    <TableRow 
                      key={lead.id} 
                      className="border-[#27272A]/40 lead-row"
                      data-testid={`lead-row-${lead.id}`}
                    >
                      <TableCell>
                        <div>
                          <p className="font-medium text-white">{lead.name}</p>
                          <div className="flex items-center gap-1 text-[#A1A1AA] text-sm mt-1">
                            <Mail className="w-3 h-3" />
                            <a href={`mailto:${lead.email}`} className="hover:text-[#D4AF37]">
                              {lead.email}
                            </a>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1 text-[#A1A1AA] text-sm">
                          <Phone className="w-3 h-3" />
                          <a href={`tel:${lead.phone}`} className="hover:text-[#D4AF37]">
                            {lead.phone}
                          </a>
                        </div>
                        {lead.message && (
                          <div className="flex items-start gap-1 text-[#52525B] text-xs mt-1 max-w-xs">
                            <MessageSquare className="w-3 h-3 mt-0.5 flex-shrink-0" />
                            <span className="truncate">{lead.message}</span>
                          </div>
                        )}
                      </TableCell>
                      <TableCell>
                        <span className="text-[#A1A1AA] text-sm capitalize">
                          {lead.source?.replace("_", " ") || "—"}
                        </span>
                      </TableCell>
                      <TableCell>
                        <Select
                          value={lead.status}
                          onValueChange={(value) => handleStatusChange(lead.id, value)}
                        >
                          <SelectTrigger 
                            className="w-32 h-8 bg-transparent border-none p-0 focus:ring-0"
                            data-testid={`status-select-${lead.id}`}
                          >
                            {getStatusBadge(lead.status)}
                          </SelectTrigger>
                          <SelectContent className="bg-[#121212] border-[#27272A]">
                            <SelectItem value="new">New</SelectItem>
                            <SelectItem value="contacted">Contacted</SelectItem>
                            <SelectItem value="converted">Converted</SelectItem>
                          </SelectContent>
                        </Select>
                      </TableCell>
                      <TableCell>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openNotesDialog(lead)}
                          data-testid={`notes-btn-${lead.id}`}
                          className="text-[#A1A1AA] hover:text-[#D4AF37] hover:bg-[#D4AF37]/10 h-8 px-2"
                        >
                          <StickyNote className="w-4 h-4 mr-1" />
                          <span className="text-xs">{lead.notes?.length || 0}</span>
                        </Button>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1 text-[#A1A1AA] text-sm">
                          <Calendar className="w-3 h-3" />
                          {formatDate(lead.created_at)}
                        </div>
                      </TableCell>
                      <TableCell>
                        <AlertDialog>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button 
                                variant="ghost" 
                                size="sm"
                                data-testid={`actions-btn-${lead.id}`}
                                className="text-[#A1A1AA] hover:text-white"
                              >
                                <MoreVertical className="w-4 h-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="bg-[#121212] border-[#27272A]">
                              <DropdownMenuItem 
                                className="text-[#A1A1AA] focus:text-white focus:bg-[#1F1F1F] cursor-pointer"
                                onClick={() => openNotesDialog(lead)}
                              >
                                <StickyNote className="w-4 h-4 mr-2" />
                                View Notes
                              </DropdownMenuItem>
                              <AlertDialogTrigger asChild>
                                <DropdownMenuItem 
                                  className="text-red-400 focus:text-red-400 focus:bg-red-400/10 cursor-pointer"
                                  data-testid={`delete-btn-${lead.id}`}
                                >
                                  <Trash2 className="w-4 h-4 mr-2" />
                                  Delete
                                </DropdownMenuItem>
                              </AlertDialogTrigger>
                            </DropdownMenuContent>
                          </DropdownMenu>
                          <AlertDialogContent className="bg-[#121212] border-[#27272A]">
                            <AlertDialogHeader>
                              <AlertDialogTitle className="text-white">Delete Lead</AlertDialogTitle>
                              <AlertDialogDescription className="text-[#A1A1AA]">
                                Are you sure you want to delete this lead? This action cannot be undone.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel className="bg-[#1F1F1F] border-[#27272A] text-white hover:bg-[#27272A]">
                                Cancel
                              </AlertDialogCancel>
                              <AlertDialogAction
                                onClick={() => handleDelete(lead.id)}
                                className="bg-red-500 text-white hover:bg-red-600"
                                data-testid={`confirm-delete-${lead.id}`}
                              >
                                Delete
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="mt-8 text-center">
          <p className="text-xs text-[#52525B]">
            {filteredLeads.length} of {leads.length} leads shown
          </p>
        </div>
      </main>

      {/* Notes Dialog */}
      <Dialog open={notesDialogOpen} onOpenChange={setNotesDialogOpen}>
        <DialogContent className="bg-[#121212] border-[#27272A] max-w-lg" data-testid="notes-dialog">
          <DialogHeader>
            <DialogTitle className="text-white" style={{ fontFamily: "'Playfair Display', serif" }}>
              Notes for {selectedLead?.name}
            </DialogTitle>
            <DialogDescription className="text-[#A1A1AA]">
              Add and manage notes for this lead
            </DialogDescription>
          </DialogHeader>
          
          {/* Add Note Form */}
          <div className="space-y-3 pt-4">
            <Textarea
              value={newNote}
              onChange={(e) => setNewNote(e.target.value)}
              placeholder="Add a note..."
              data-testid="note-input"
              className="bg-[#1F1F1F] border-[#27272A]/40 focus:border-[#D4AF37] rounded-sm text-white placeholder:text-[#52525B] resize-none min-h-[80px]"
            />
            <Button
              onClick={handleAddNote}
              disabled={!newNote.trim() || isAddingNote}
              data-testid="add-note-btn"
              className="w-full bg-[#D4AF37] text-black hover:bg-[#D4AF37]/90 rounded-none uppercase tracking-widest text-xs font-bold disabled:opacity-50"
            >
              <Plus className="w-4 h-4 mr-2" />
              {isAddingNote ? "Adding..." : "Add Note"}
            </Button>
          </div>

          {/* Notes List */}
          <div className="pt-4 border-t border-[#27272A]/40 mt-4">
            <p className="text-xs text-[#A1A1AA] uppercase tracking-wider mb-3">
              {selectedLead?.notes?.length || 0} Notes
            </p>
            {selectedLead?.notes?.length > 0 ? (
              <ScrollArea className="h-[250px] pr-4">
                <div className="space-y-3">
                  {selectedLead.notes.slice().reverse().map((note) => (
                    <div 
                      key={note.id} 
                      className="bg-[#1F1F1F] border border-[#27272A]/40 rounded-sm p-4 group"
                      data-testid={`note-item-${note.id}`}
                    >
                      <div className="flex justify-between items-start gap-2">
                        <p className="text-white text-sm leading-relaxed flex-1">{note.text}</p>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDeleteNote(note.id)}
                          data-testid={`delete-note-${note.id}`}
                          className="opacity-0 group-hover:opacity-100 transition-opacity text-[#52525B] hover:text-red-400 hover:bg-red-400/10 h-6 w-6 p-0"
                        >
                          <X className="w-3 h-3" />
                        </Button>
                      </div>
                      <p className="text-xs text-[#52525B] mt-2">
                        {formatNoteDate(note.created_at)}
                      </p>
                    </div>
                  ))}
                </div>
              </ScrollArea>
            ) : (
              <div className="text-center py-8">
                <StickyNote className="w-8 h-8 text-[#27272A] mx-auto mb-2" />
                <p className="text-sm text-[#52525B]">No notes yet</p>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminDashboard;
